import React, { useCallback, useEffect, useState } from "react";
import {
  CreditCard,
  ExternalLink,
  Loader2,
  Plus,
  RefreshCw,
  Send,
  X,
} from "lucide-react";
import { toast } from "react-toastify";
import { usePhoneAccount } from "../contexts/PhoneAccountContext";
import {
  createPaymentConfiguration,
  generatePaymentOauthLink,
  listPaymentConfigurations,
  listPaymentTransactions,
  sendPayment,
} from "../apis/payments";

function apiError(err, fallback) {
  return (
    err?.response?.data?.error ||
    err?.response?.data?.details ||
    err?.response?.data?.message ||
    err?.message ||
    fallback
  );
}

function pickTosUrl(err) {
  return err?.response?.data?.tosUrl || null;
}

function formatTs(ts) {
  if (!ts) return "—";
  const n = Number(ts);
  if (!n) return "—";
  const ms = n < 1e12 ? n * 1000 : n;
  return new Date(ms).toLocaleString();
}

function codeLabel(value) {
  if (!value) return "—";
  if (typeof value === "string") return value;
  const code = value.code || "";
  const desc = value.description || "";
  if (code && desc) return `${code} · ${desc}`;
  return code || desc || "—";
}

function defaultRedirectUrl() {
  return `${window.location.origin}/whatsapp-payments`;
}

const PROVIDERS = [
  { value: "razorpay", label: "Razorpay" },
  { value: "payu", label: "PayU" },
];

const WhatsAppPayments = () => {
  const { selectedPhoneId } = usePhoneAccount();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [oauthBusy, setOauthBusy] = useState("");
  const [tosError, setTosError] = useState(null);
  const [sendFor, setSendFor] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendForm, setSendForm] = useState({
    to: "",
    amount: "1",
    item_name: "Test payment",
    body: "",
  });
  const [transactions, setTransactions] = useState([]);
  const [form, setForm] = useState({
    configuration_name: "",
    provider_name: "razorpay",
    purpose_code: "00",
    merchant_category_code: "0000",
    redirect_url: "",
  });

  const loadConfigs = useCallback(async () => {
    if (!selectedPhoneId) {
      setConfigs([]);
      setTransactions([]);
      return;
    }
    setLoading(true);
    setTosError(null);
    try {
      const [res, txRes] = await Promise.all([
        listPaymentConfigurations(selectedPhoneId),
        listPaymentTransactions(selectedPhoneId).catch(() => ({ data: { transactions: [] } })),
      ]);
      setConfigs(res.data?.configurations || []);
      setTransactions(txRes.data?.transactions || []);
    } catch (err) {
      console.error("Failed to load payment configurations:", err);
      const tosUrl = pickTosUrl(err);
      const message = apiError(err, "Failed to load payment configurations");
      if (tosUrl || /terms of service/i.test(message)) {
        setTosError({ message, tosUrl });
      } else {
        toast.error(message);
      }
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  }, [selectedPhoneId]);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      redirect_url: prev.redirect_url || defaultRedirectUrl(),
    }));
  }, []);

  const openOauth = (url) => {
    if (!url) {
      toast.error("No OAuth URL returned");
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const configuration_name = form.configuration_name.trim();
    const provider_name = form.provider_name.trim();
    if (!configuration_name || !provider_name) {
      toast.error("Name and provider are required");
      return;
    }
    setCreating(true);
    try {
      const res = await createPaymentConfiguration(selectedPhoneId, {
        configuration_name,
        provider_name,
        purpose_code: form.purpose_code.trim() || undefined,
        merchant_category_code: form.merchant_category_code.trim() || undefined,
        redirect_url: form.redirect_url.trim() || defaultRedirectUrl(),
      });
      toast.success("Payment configuration created. Complete gateway onboarding.");
      setShowCreate(false);
      if (res.data?.oauth_url) openOauth(res.data.oauth_url);
      await loadConfigs();
    } catch (err) {
      toast.error(apiError(err, "Failed to create payment configuration"));
    } finally {
      setCreating(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!sendFor) return;
    setSending(true);
    try {
      const res = await sendPayment(selectedPhoneId, {
        configuration_name: sendFor.name,
        provider_name: sendFor.provider,
        to: sendForm.to,
        amount: sendForm.amount,
        item_name: sendForm.item_name,
        body: sendForm.body,
      });
      toast.success(`Payment request sent. Ref ${res.data?.reference_id || ""}`);
      setSendFor(null);
    } catch (err) {
      toast.error(apiError(err, "Failed to send payment message"));
    } finally {
      setSending(false);
    }
  };

  const handleOauth = async (configuration_name) => {
    setOauthBusy(configuration_name);
    try {
      const res = await generatePaymentOauthLink(selectedPhoneId, {
        configuration_name,
        redirect_url: defaultRedirectUrl(),
      });
      openOauth(res.data?.oauth_url);
    } catch (err) {
      toast.error(apiError(err, "Failed to generate OAuth link"));
    } finally {
      setOauthBusy("");
    }
  };

  const activeCount = configs.filter((c) => String(c.status || "").toLowerCase() === "active").length;
  const statusTone = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "active" || s === "captured") return "g";
    if (s.includes("test") || s.includes("pending")) return "a";
    if (s.includes("fail") || s.includes("error")) return "r";
    return "n";
  };

  return (
    <div className="pay-page fl-page">
      <div className="page-head">
        <div>
          <h2>WhatsApp Payments</h2>
          <p>Link a payment gateway so customers can pay inside WhatsApp</p>
        </div>
        <div className="grow" />
        <button
          type="button"
          onClick={loadConfigs}
          disabled={!selectedPhoneId || loading}
          className="ping-btn ping-btn-ghost ping-btn-sm"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
          Refresh
        </button>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          disabled={!selectedPhoneId || !!tosError}
          className="ping-btn ping-btn-green ping-btn-sm"
        >
          <Plus size={16} />
          New configuration
        </button>
      </div>

      {tosError && (
        <div className="ping-banner amber">
          <div>
            <b>WhatsApp Payments terms are not accepted yet</b>
            <p>
              Meta requires the Business Manager admin to accept WhatsApp Payments Terms of Service
              for this WABA before configurations can be listed or created.
            </p>
            {tosError.tosUrl && (
              <a href={tosError.tosUrl} target="_blank" rel="noreferrer">
                Accept terms on Facebook <ExternalLink size={13} />
              </a>
            )}
            <p>After accepting, wait a minute and click Refresh.</p>
          </div>
        </div>
      )}

      {selectedPhoneId && (
        <div className="fl-kpis">
          <div className="fl-kpi hi">
            <div>
              <div className="k">Configurations</div>
              <div className="v">{configs.length}</div>
            </div>
            <div className="ico"><CreditCard size={14} /></div>
          </div>
          <div className="fl-kpi">
            <div>
              <div className="k">Active</div>
              <div className="v">{activeCount}</div>
            </div>
            <div className="ico"><Send size={14} /></div>
          </div>
          <div className="fl-kpi">
            <div>
              <div className="k">Received</div>
              <div className="v">{transactions.length}</div>
            </div>
            <div className="ico"><RefreshCw size={14} /></div>
          </div>
        </div>
      )}

      {!selectedPhoneId ? (
        <div className="fl-empty">
          <div className="fl-empty-ico"><CreditCard size={22} /></div>
          <h3>Select a number</h3>
          <p>Choose a WhatsApp number in the sidebar to manage payments.</p>
        </div>
      ) : loading && configs.length === 0 ? (
        <div className="fl-grid">
          {[1, 2, 3].map((i) => (
            <div key={i} className="fl-card fl-skel">
              <div className="fl-skel-line" />
              <div className="fl-skel-line short" />
            </div>
          ))}
        </div>
      ) : configs.length === 0 ? (
        <div className="fl-empty">
          <div className="fl-empty-ico"><CreditCard size={22} /></div>
          <h3>No payment configurations yet</h3>
          <p>Create one, then complete Razorpay / Facebook onboarding.</p>
          {!tosError && (
            <button type="button" onClick={() => setShowCreate(true)} className="ping-btn ping-btn-green ping-btn-sm">
              <Plus size={16} />
              New configuration
            </button>
          )}
        </div>
      ) : (
        <div className="fl-grid">
          {configs.map((cfg) => {
            const name = cfg.configuration_name || cfg.name;
            const status = cfg.status || "Unknown";
            return (
              <article key={name} className="fl-card">
                <div className="fl-card-top">
                  <h3>{name}</h3>
                  <span className={`ping-chip ${statusTone(status)}`}>{status}</span>
                </div>
                <p className="fl-card-desc">
                  {cfg.provider_name || "—"}
                  {cfg.provider_mid ? ` · ${cfg.provider_mid}` : ""}
                </p>
                <div className="fl-card-chips">
                  <span className="ping-chip n">MCC {codeLabel(cfg.merchant_category_code)}</span>
                  <span className="ping-chip b">Purpose {codeLabel(cfg.purpose_code)}</span>
                </div>
                <div className="fl-card-foot">
                  <span className="fl-date">{formatTs(cfg.updated_timestamp)}</span>
                  <div className="fl-actions">
                    <button
                      type="button"
                      className="camp-ico"
                      data-tip="OAuth"
                      aria-label="OAuth"
                      disabled={oauthBusy === name}
                      onClick={() => handleOauth(name)}
                    >
                      {oauthBusy === name ? <Loader2 size={14} className="animate-spin" /> : <ExternalLink size={14} />}
                    </button>
                    <button
                      type="button"
                      className="ping-btn ping-btn-green ping-btn-sm"
                      onClick={() => {
                        setSendFor({ name, provider: cfg.provider_name });
                        setSendForm({
                          to: "",
                          amount: "1",
                          item_name: "Test payment",
                          body: "",
                        });
                      }}
                    >
                      <Send size={14} />
                      Send
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {selectedPhoneId && !tosError && (
        <section className="pay-tx">
          <div className="pay-tx-head">
            <h3>Payments received</h3>
            <span className="ping-chip n">{transactions.length}</span>
          </div>
          {transactions.length === 0 ? (
            <div className="fl-empty pay-tx-empty">
              <p>No captured payments yet. After a customer pays, it will show here.</p>
            </div>
          ) : (
            <div className="pay-table-wrap">
              <table className="pay-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Customer</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Method</th>
                    <th>Reference</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx) => {
                    const rupees =
                      tx.amount_value != null
                        ? (Number(tx.amount_value) / Number(tx.amount_offset || 100)).toFixed(2)
                        : "—";
                    const when = tx.status_timestamp || tx.created_at;
                    return (
                      <tr key={tx.id}>
                        <td>{when ? new Date(String(when).replace(" ", "T") + "Z").toLocaleString() : "—"}</td>
                        <td className="mono">{tx.recipient_wa_id || "—"}</td>
                        <td className="amt">₹{rupees}</td>
                        <td>
                          <span className={`ping-chip ${statusTone(tx.status)}`}>{tx.status}</span>
                        </td>
                        <td>{tx.payment_method || "—"}</td>
                        <td className="mono">{tx.reference_id || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {showCreate && (
        <>
          <div className="ping-drawer-backdrop" onClick={() => setShowCreate(false)} />
          <aside className="ping-drawer" role="dialog" aria-modal="true" aria-label="New payment configuration">
            <form onSubmit={handleCreate} className="pay-drawer-form">
              <div className="ping-drawer-head">
                <div>
                  <h3>New configuration</h3>
                  <p>Connect a gateway for WhatsApp checkout</p>
                </div>
                <button type="button" className="camp-ico" onClick={() => setShowCreate(false)} aria-label="Close">
                  <X size={16} />
                </button>
              </div>
              <div className="ping-drawer-body">
                <label>Configuration name</label>
                <input
                  value={form.configuration_name}
                  onChange={(e) => setForm((p) => ({ ...p, configuration_name: e.target.value }))}
                  placeholder="store-upi"
                />
                <label>Provider</label>
                <select
                  value={form.provider_name}
                  onChange={(e) => setForm((p) => ({ ...p, provider_name: e.target.value }))}
                >
                  {PROVIDERS.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
                <div className="pay-split">
                  <div>
                    <label>Purpose code</label>
                    <input
                      value={form.purpose_code}
                      onChange={(e) => setForm((p) => ({ ...p, purpose_code: e.target.value }))}
                      placeholder="00"
                    />
                  </div>
                  <div>
                    <label>MCC</label>
                    <input
                      value={form.merchant_category_code}
                      onChange={(e) => setForm((p) => ({ ...p, merchant_category_code: e.target.value }))}
                      placeholder="0000"
                    />
                  </div>
                </div>
                <label>Redirect URL</label>
                <input
                  value={form.redirect_url}
                  onChange={(e) => setForm((p) => ({ ...p, redirect_url: e.target.value }))}
                />
                <p className="pay-hint">Facebook sends the merchant back here after gateway onboarding.</p>
              </div>
              <div className="ping-drawer-foot">
                <button type="button" className="ping-btn ping-btn-ghost ping-btn-sm" onClick={() => setShowCreate(false)}>
                  Cancel
                </button>
                <button type="submit" disabled={creating} className="ping-btn ping-btn-green ping-btn-sm">
                  {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                  {creating ? "Creating…" : "Create & connect"}
                </button>
              </div>
            </form>
          </aside>
        </>
      )}

      {sendFor && (
        <>
          <div className="ping-drawer-backdrop" onClick={() => setSendFor(null)} />
          <aside className="ping-drawer" role="dialog" aria-modal="true" aria-label="Send payment">
            <form onSubmit={handleSend} className="pay-drawer-form">
              <div className="ping-drawer-head">
                <div>
                  <h3>Send payment</h3>
                  <p>Uses {sendFor.name}. Customer must have messaged in the last 24 hours.</p>
                </div>
                <button type="button" className="camp-ico" onClick={() => setSendFor(null)} aria-label="Close">
                  <X size={16} />
                </button>
              </div>
              <div className="ping-drawer-body">
                <label>Customer WhatsApp number</label>
                <input
                  value={sendForm.to}
                  onChange={(e) => setSendForm((p) => ({ ...p, to: e.target.value }))}
                  placeholder="9998012345 or 919998012345"
                />
                <div className="pay-split">
                  <div>
                    <label>Amount (₹)</label>
                    <input
                      type="number"
                      min="1"
                      step="0.01"
                      value={sendForm.amount}
                      onChange={(e) => setSendForm((p) => ({ ...p, amount: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label>Item name</label>
                    <input
                      value={sendForm.item_name}
                      onChange={(e) => setSendForm((p) => ({ ...p, item_name: e.target.value }))}
                    />
                  </div>
                </div>
                <label>Message (optional)</label>
                <textarea
                  value={sendForm.body}
                  onChange={(e) => setSendForm((p) => ({ ...p, body: e.target.value }))}
                  rows={3}
                  placeholder="Please pay for your order"
                />
              </div>
              <div className="ping-drawer-foot">
                <button type="button" className="ping-btn ping-btn-ghost ping-btn-sm" onClick={() => setSendFor(null)}>
                  Cancel
                </button>
                <button type="submit" disabled={sending} className="ping-btn ping-btn-green ping-btn-sm">
                  {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  {sending ? "Sending…" : "Send"}
                </button>
              </div>
            </form>
          </aside>
        </>
      )}
    </div>
  );
};

export default WhatsAppPayments;
